/**
 * The GST rates a line or a charge may carry, as decimal strings.
 *
 * The slabs since 22 Sep 2025 are 0, 5, 18 and 40, with the special rates
 * below them; 12 and 28 stay listed because older entries and a few goods
 * still carry them. The list is for checking a rate the person picked
 * (`settings.charges`, D17) and for reading a formula's constant (D15:
 * 1.18 is "price plus 18% tax", 0.18 is "the tax alone"). It never sets a
 * rate on a line: the rate is the line's own field.
 *
 * A rate is held in basis points (hundredths of a percent) so that 0.25% and
 * 7.5% stay integers and no float ever touches tax.
 */

export const GST_RATES = ['0', '0.1', '0.25', '1', '1.5', '3', '5', '6', '7.5', '12', '18', '28', '40'] as const;
export type GstRate = (typeof GST_RATES)[number];

const RATE_TEXT = /^(\d{1,3})(?:\.(\d{1,2}))?$/;

/** A percentage as basis points (`'18'` gives 1800, `'0.25'` gives 25), or null when it is not a plain percentage. */
export const rateBasisPoints = (text: string | number | null | undefined): number | null => {
  if (text === null || text === undefined) return null;
  const raw = typeof text === 'number' ? (Number.isFinite(text) ? String(text) : '') : text.trim().replace(/%$/, '').trim();
  const match = RATE_TEXT.exec(raw);
  if (match === null) return null;
  const whole = Number(match[1]);
  const frac = Number((match[2] ?? '').padEnd(2, '0'));
  const bp = whole * 100 + frac;
  return bp <= 10_000 ? bp : null;
};

const RATE_BP: ReadonlySet<number> = new Set(GST_RATES.map((r) => rateBasisPoints(r)!));

/** True for a rate on the GST list, as text or a number (`'18'`, `18`, `'18%'`). */
export const isGstRate = (text: string | number | null | undefined): boolean => {
  const bp = rateBasisPoints(text);
  return bp !== null && RATE_BP.has(bp);
};

/** True for basis points on the GST list. */
export const isGstRateBasisPoints = (bp: number): boolean => RATE_BP.has(bp);

/** Every rate on the list, as basis points, lowest first. */
export const GST_RATE_LIST_BP: readonly number[] = [...RATE_BP].sort((a, b) => a - b);
